import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, RefreshCw, Search } from "lucide-react";
import { safeFetch } from "../utils/api";

const DEFAULT_FILTERS = {
    provider: "",
    league_group_id: "",
    league_id: "",
    canonical_id: "",
    year: "",
    week: "",
    matchup_period: "",
    owner: "",
    opponent: "",
    location: "",
    is_regular_season: "",
    is_playoff: "",
    box_score_available: "",
    min_score: "",
    max_score: "",
    ordering: "-year,-week,league,owner",
};

const PAGE_SIZE = 100;

const boolOptions = [
    { value: "", label: "Any" },
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
];

const AdminHistoricalStorePage = () => {
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [offset, setOffset] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const queryString = useMemo(() => {
        const params = new URLSearchParams();
        Object.entries(appliedFilters).forEach(([key, value]) => {
            if (value !== "") params.set(key, value);
        });
        params.set("limit", PAGE_SIZE);
        params.set("offset", offset);
        return params.toString();
    }, [appliedFilters, offset]);

    const load = () => {
        setLoading(true);
        setError(null);
        safeFetch(`/api/admin/historical-store/?${queryString}`, {}, false, 1)
            .then((payload) => setData(payload))
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryString]);

    const setFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const applyFilters = (event) => {
        event.preventDefault();
        setOffset(0);
        setAppliedFilters(filters);
    };

    const resetFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
        setOffset(0);
    };

    const rows = data?.rows || [];
    const groups = data?.league_groups || [];
    const totalRows = data?.total ?? rows.length;
    const resultOffset = data?.offset ?? offset;
    const resultLimit = data?.limit ?? PAGE_SIZE;
    const from = totalRows ? resultOffset + 1 : 0;
    const to = data ? Math.min(resultOffset + resultLimit, totalRows) : 0;

    return (
        <div className="min-h-screen overflow-x-hidden bg-surface px-3 py-6 sm:px-4 sm:py-8">
            <div className="mx-auto max-w-7xl min-w-0 space-y-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                        <h1 className="text-2xl font-bold text-forest">
                            Historical Store
                        </h1>
                        <p className="text-sm text-text-muted mt-1">
                            {data ? `${totalRows} rows` : "Loading rows"}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        className="inline-flex w-full items-center justify-center gap-2 rounded border border-border bg-white px-3 py-2 text-sm font-semibold text-text hover:bg-surface sm:w-auto"
                    >
                        <RefreshCw size={16} />
                        Refresh
                    </button>
                </div>

                <form
                    onSubmit={applyFilters}
                    className="min-w-0 rounded-lg border border-border bg-white p-4"
                >
                    <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <Field label="Provider">
                            <select
                                value={filters.provider}
                                onChange={(e) => setFilter("provider", e.target.value)}
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            >
                                <option value="">Any</option>
                                <option value="espn">ESPN</option>
                                <option value="sleeper">Sleeper</option>
                                <option value="yahoo">Yahoo</option>
                            </select>
                        </Field>
                        <Field label="League Group">
                            <select
                                value={filters.league_group_id}
                                onChange={(e) =>
                                    setFilter("league_group_id", e.target.value)
                                }
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            >
                                <option value="">Any</option>
                                {groups.map((group) => (
                                    <option key={group.id} value={group.id}>
                                        {group.provider}: {group.name}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="League ID">
                            <input
                                value={filters.league_id}
                                onChange={(e) => setFilter("league_id", e.target.value)}
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            />
                        </Field>
                        <Field label="Canonical ID">
                            <input
                                value={filters.canonical_id}
                                onChange={(e) =>
                                    setFilter("canonical_id", e.target.value)
                                }
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            />
                        </Field>
                        <Field label="Year">
                            <input
                                value={filters.year}
                                onChange={(e) => setFilter("year", e.target.value)}
                                inputMode="numeric"
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            />
                        </Field>
                        <Field label="Week">
                            <input
                                value={filters.week}
                                onChange={(e) => setFilter("week", e.target.value)}
                                inputMode="numeric"
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            />
                        </Field>
                        <Field label="Matchup Period">
                            <input
                                value={filters.matchup_period}
                                onChange={(e) =>
                                    setFilter("matchup_period", e.target.value)
                                }
                                inputMode="numeric"
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            />
                        </Field>
                        <Field label="Location">
                            <select
                                value={filters.location}
                                onChange={(e) => setFilter("location", e.target.value)}
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            >
                                <option value="">Any</option>
                                <option value="HOME">HOME</option>
                                <option value="AWAY">AWAY</option>
                            </select>
                        </Field>
                        <Field label="Owner">
                            <input
                                value={filters.owner}
                                onChange={(e) => setFilter("owner", e.target.value)}
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            />
                        </Field>
                        <Field label="Opponent">
                            <input
                                value={filters.opponent}
                                onChange={(e) => setFilter("opponent", e.target.value)}
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            />
                        </Field>
                        <Field label="Min Score">
                            <input
                                value={filters.min_score}
                                onChange={(e) => setFilter("min_score", e.target.value)}
                                inputMode="decimal"
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            />
                        </Field>
                        <Field label="Max Score">
                            <input
                                value={filters.max_score}
                                onChange={(e) => setFilter("max_score", e.target.value)}
                                inputMode="decimal"
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            />
                        </Field>
                        <Field label="Regular Season">
                            <BooleanSelect
                                value={filters.is_regular_season}
                                onChange={(value) =>
                                    setFilter("is_regular_season", value)
                                }
                            />
                        </Field>
                        <Field label="Playoff">
                            <BooleanSelect
                                value={filters.is_playoff}
                                onChange={(value) => setFilter("is_playoff", value)}
                            />
                        </Field>
                        <Field label="Box Score">
                            <BooleanSelect
                                value={filters.box_score_available}
                                onChange={(value) =>
                                    setFilter("box_score_available", value)
                                }
                            />
                        </Field>
                        <Field label="Order">
                            <select
                                value={filters.ordering}
                                onChange={(e) => setFilter("ordering", e.target.value)}
                                className="w-full rounded border border-border px-2 py-2 text-sm"
                            >
                                <option value="-year,-week,league,owner">
                                    Year, week desc
                                </option>
                                <option value="provider,league,year,week,owner">
                                    Provider, league
                                </option>
                                <option value="-score">Score desc</option>
                                <option value="score">Score asc</option>
                                <option value="-fetched_at">Fetched desc</option>
                            </select>
                        </Field>
                    </div>
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                        <button
                            type="submit"
                            className="inline-flex items-center justify-center gap-2 rounded bg-forest px-4 py-2 text-sm font-semibold text-white hover:bg-forest/90"
                        >
                            <Search size={16} />
                            Search
                        </button>
                        <button
                            type="button"
                            onClick={resetFilters}
                            className="rounded border border-border bg-white px-4 py-2 text-sm font-semibold text-text hover:bg-surface"
                        >
                            Reset
                        </button>
                    </div>
                </form>

                {error && (
                    <div className="rounded-lg border border-coral bg-coral-light p-3 text-sm text-coral">
                        {error}
                    </div>
                )}

                <div className="min-w-0 overflow-hidden rounded-lg border border-border bg-white">
                    <div className="flex flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm font-semibold text-text">
                            {loading ? "Loading..." : `${from}-${to} of ${totalRows}`}
                        </p>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={offset <= 0 || loading}
                                onClick={() =>
                                    setOffset((current) =>
                                        Math.max(current - PAGE_SIZE, 0)
                                    )
                                }
                                className="inline-flex h-9 w-9 items-center justify-center rounded border border-border text-text disabled:opacity-40"
                                title="Previous page"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                type="button"
                                disabled={!data?.has_next || loading}
                                onClick={() =>
                                    setOffset((current) => current + PAGE_SIZE)
                                }
                                className="inline-flex h-9 w-9 items-center justify-center rounded border border-border text-text disabled:opacity-40"
                                title="Next page"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>

                    <div className="space-y-3 p-3 md:hidden">
                        {rows.map((row) => (
                            <article
                                key={row.id}
                                className="min-w-0 rounded border border-border bg-white p-3"
                            >
                                <div className="min-w-0 border-b border-border pb-3">
                                    <p className="break-words font-semibold text-text">
                                        {row.league_name}
                                    </p>
                                    <p className="mt-1 break-words text-xs text-text-muted">
                                        {row.provider} / {row.canonical_id}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-3 py-3 text-sm">
                                    <MobileValue label="Year" value={row.year} />
                                    <MobileValue label="Week" value={row.week} />
                                    <MobileValue
                                        label="Period"
                                        value={row.matchup_period}
                                    />
                                    <MobileValue
                                        label="Fetched"
                                        value={formatDate(row.fetched_at)}
                                    />
                                </div>

                                <div className="space-y-3 border-t border-border pt-3">
                                    <MatchupSide
                                        label="Team"
                                        owner={row.team_owner}
                                        name={row.team_name}
                                        score={row.team_score}
                                    />
                                    <MatchupSide
                                        label="Opponent"
                                        owner={row.opp_owner}
                                        name={row.opp_name}
                                        score={row.opp_score}
                                    />
                                </div>

                                <div className="mt-3 rounded bg-surface px-3 py-2 text-xs text-text-muted">
                                    {formatFlags(row)}
                                </div>
                            </article>
                        ))}
                        {!loading && rows.length === 0 && (
                            <div className="rounded border border-dashed border-border px-3 py-8 text-center text-sm text-text-muted">
                                No rows found.
                            </div>
                        )}
                    </div>

                    <div className="hidden md:block">
                        <div className="border-b border-border bg-surface/60 px-4 py-2 text-xs text-text-muted">
                            Scroll sideways inside this table to inspect all columns.
                        </div>
                        <div className="max-w-full overflow-x-auto overscroll-x-contain">
                            <table className="w-full min-w-[960px] text-sm">
                                <thead className="bg-surface text-left text-xs uppercase text-text-muted">
                                    <tr>
                                        <th className="px-3 py-2">League</th>
                                        <th className="px-3 py-2">Year</th>
                                        <th className="px-3 py-2">Week</th>
                                        <th className="px-3 py-2">Period</th>
                                        <th className="px-3 py-2">Team</th>
                                        <th className="px-3 py-2 text-right">Score</th>
                                        <th className="px-3 py-2">Opponent</th>
                                        <th className="px-3 py-2 text-right">Opp Score</th>
                                        <th className="px-3 py-2">Flags</th>
                                        <th className="px-3 py-2">Fetched</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr
                                            key={row.id}
                                            className="border-t border-border hover:bg-surface/60"
                                        >
                                            <td className="px-3 py-2 align-top">
                                                <div className="font-semibold text-text">
                                                    {row.league_name}
                                                </div>
                                                <div className="text-xs text-text-muted">
                                                    {row.provider} / {row.canonical_id}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                {row.year}
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                {row.week}
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                {row.matchup_period}
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <div className="font-medium text-text">
                                                    {row.team_owner}
                                                </div>
                                                <div className="text-xs text-text-muted">
                                                    {row.team_name}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-right align-top font-semibold">
                                                {formatScore(row.team_score)}
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <div className="font-medium text-text">
                                                    {row.opp_owner}
                                                </div>
                                                <div className="text-xs text-text-muted">
                                                    {row.opp_name}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-right align-top">
                                                {formatScore(row.opp_score)}
                                            </td>
                                            <td className="px-3 py-2 align-top text-xs text-text-muted">
                                                {formatFlags(row)}
                                            </td>
                                            <td className="px-3 py-2 align-top text-xs text-text-muted">
                                                {formatDate(row.fetched_at)}
                                            </td>
                                        </tr>
                                    ))}
                                    {!loading && rows.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan="10"
                                                className="px-3 py-8 text-center text-text-muted"
                                            >
                                                No rows found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Field = ({ label, children }) => (
    <label className="block min-w-0">
        <span className="mb-1 block text-xs font-semibold uppercase text-text-muted">
            {label}
        </span>
        {children}
    </label>
);

const BooleanSelect = ({ value, onChange }) => (
    <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-border px-2 py-2 text-sm"
    >
        {boolOptions.map((option) => (
            <option key={option.value} value={option.value}>
                {option.label}
            </option>
        ))}
    </select>
);

const formatScore = (value) => {
    if (value === null || value === undefined) return "";
    return Number(value).toFixed(2);
};

const formatDate = (value) => {
    if (!value) return "";
    return new Date(value).toLocaleString();
};

const formatFlags = (row) => {
    const flags = [row.location || "NA"];
    if (row.is_playoff) flags.push("Playoff");
    if (row.is_multiweek_matchup) flags.push("Multiweek");
    if (!row.box_score_available) flags.push("No box");
    return flags.join(" / ");
};

const MobileValue = ({ label, value }) => (
    <div className="min-w-0">
        <p className="text-xs font-semibold uppercase text-text-muted">{label}</p>
        <p className="mt-1 break-words text-text">{value || "NA"}</p>
    </div>
);

const MatchupSide = ({ label, owner, name, score }) => (
    <div className="grid min-w-0 grid-cols-[1fr_auto] gap-3">
        <div className="min-w-0">
            <p className="text-xs font-semibold uppercase text-text-muted">{label}</p>
            <p className="mt-1 break-words font-medium text-text">{owner || "NA"}</p>
            <p className="mt-0.5 break-words text-xs text-text-muted">{name || "NA"}</p>
        </div>
        <div className="text-right">
            <p className="text-xs font-semibold uppercase text-text-muted">Score</p>
            <p className="mt-1 font-semibold text-text">{formatScore(score) || "NA"}</p>
        </div>
    </div>
);

export default AdminHistoricalStorePage;
